// 🎯 CRM API service - chamadas para o backend Flask
// Acesso restrito a admin (verificado no backend).

const getEmpresaId = (session: any): string => {
  return session?.empresa?.id || session?.usuario?.empresaId || '';
};

const getUserEmail = (session: any): string => {
  return session?.usuario?.email || session?.user?.email || '';
};

const buildQuery = (session: any, extra: Record<string, string> = {}): string => {
  const params = new URLSearchParams({
    empresaId: getEmpresaId(session),
    userEmail: getUserEmail(session),
    ...extra,
  });
  return params.toString();
};

const handle = async (resp: Response) => {
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || data?.success === false) {
    throw new Error(data?.error || `HTTP ${resp.status}`);
  }
  return data;
};

// ---------------- Tipos ----------------
export interface FunilEtapa {
  id: string;
  nome: string;
  cor: string;
  probabilidade: number;
}

export interface Funil {
  id: string;
  nome: string;
  descricao?: string;
  etapas: FunilEtapa[];
  empresaId: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Negocio {
  id: string;
  empresaId: string;
  funilId: string;
  etapaId: string;
  titulo: string;
  valor: number;
  cliente?: string;
  contatoId?: string | null;
  empresaContaId?: string | null;
  email?: string;
  telefone?: string;
  observacoes?: string;
  etiquetas?: string[];
  camposCustom?: Record<string, any>;
  responsavelId?: string | null;
  dataFechamentoPrevista?: string | null;
  origem?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Contato {
  id: string;
  empresaId: string;
  nome: string;
  email?: string;
  telefone?: string;
  empresa?: string;
  cargo?: string;
  empresaContaId?: string | null;
  responsavelId?: string | null;
  tipo: 'cliente' | 'prospect' | 'parceiro' | 'fornecedor';
  endereco?: { rua?: string; cidade?: string; estado?: string; cep?: string };
  observacoes?: string;
  etiquetas?: string[];
  camposCustom?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

export interface Lead {
  id: string;
  empresaId: string;
  nome: string;
  email?: string;
  telefone?: string;
  empresa?: string;
  origem?: string;
  status: 'novo' | 'em_contato' | 'qualificado' | 'nao_qualificado' | 'convertido';
  temperatura?: 'frio' | 'morno' | 'quente';
  responsavelId?: string | null;
  observacoes?: string;
  etiquetas?: string[];
  camposCustom?: Record<string, any>;
  convertidoEm?: string | null;
  contatoId?: string | null;
  empresaContaId?: string | null;
  negocioId?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface EmpresaConta {
  id: string;
  empresaId: string;
  razaoSocial: string;
  nomeFantasia?: string;
  cnpj?: string;
  site?: string;
  telefone?: string;
  email?: string;
  ramo?: string;
  porte?: 'micro' | 'pequena' | 'media' | 'grande' | '';
  responsavelId?: string | null;
  endereco?: { rua?: string; cidade?: string; estado?: string; cep?: string };
  observacoes?: string;
  etiquetas?: string[];
  camposCustom?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

export interface Etiqueta {
  id: string;
  empresaId: string;
  nome: string;
  cor: string;
}

export interface CampoCustom {
  id: string;
  empresaId: string;
  entidade: 'lead' | 'contato' | 'negocio' | 'empresa-conta';
  nome: string;
  chave: string;
  tipo: 'texto' | 'numero' | 'select' | 'data' | 'checkbox';
  opcoes?: string[];
  obrigatorio?: boolean;
  ordem?: number;
}

// ---------------- API ----------------
const jsonHeaders = { 'Content-Type': 'application/json' };

export const crmApi = {
  // Funis
  listarFunis: async (session: any): Promise<Funil[]> => {
    const r = await fetch(`/api/crm/funis?${buildQuery(session)}`);
    const data = await handle(r);
    return data.funis || [];
  },
  criarFunil: async (session: any, nome: string, descricao?: string): Promise<Funil> => {
    const r = await fetch(`/api/crm/funis?${buildQuery(session)}`, {
      method: 'POST', headers: jsonHeaders,
      body: JSON.stringify({ empresaId: getEmpresaId(session), nome, descricao })
    });
    const data = await handle(r);
    return data.funil;
  },
  atualizarFunil: async (session: any, id: string, payload: Partial<Funil>): Promise<void> => {
    const r = await fetch(`/api/crm/funis/${id}?${buildQuery(session)}`, {
      method: 'PUT', headers: jsonHeaders,
      body: JSON.stringify({ ...payload, empresaId: getEmpresaId(session) })
    });
    await handle(r);
  },
  removerFunil: async (session: any, id: string): Promise<void> => {
    const r = await fetch(`/api/crm/funis/${id}?${buildQuery(session)}`, { method: 'DELETE' });
    await handle(r);
  },

  // Negócios
  listarNegocios: async (session: any, funilId?: string): Promise<Negocio[]> => {
    const extra = funilId ? { funilId } : {};
    const r = await fetch(`/api/crm/negocios?${buildQuery(session, extra)}`);
    const data = await handle(r);
    return data.negocios || [];
  },
  criarNegocio: async (session: any, payload: Partial<Negocio>): Promise<Negocio> => {
    const r = await fetch(`/api/crm/negocios?${buildQuery(session)}`, {
      method: 'POST', headers: jsonHeaders,
      body: JSON.stringify({ ...payload, empresaId: getEmpresaId(session) })
    });
    const data = await handle(r);
    return data.negocio;
  },
  atualizarNegocio: async (session: any, id: string, payload: Partial<Negocio>): Promise<void> => {
    const r = await fetch(`/api/crm/negocios/${id}?${buildQuery(session)}`, {
      method: 'PUT', headers: jsonHeaders,
      body: JSON.stringify({ ...payload, empresaId: getEmpresaId(session) })
    });
    await handle(r);
  },
  removerNegocio: async (session: any, id: string): Promise<void> => {
    const r = await fetch(`/api/crm/negocios/${id}?${buildQuery(session)}`, { method: 'DELETE' });
    await handle(r);
  },

  // Contatos
  listarContatos: async (session: any): Promise<Contato[]> => {
    const r = await fetch(`/api/crm/contatos?${buildQuery(session)}`);
    const data = await handle(r);
    return data.contatos || [];
  },
  criarContato: async (session: any, payload: Partial<Contato>): Promise<Contato> => {
    const r = await fetch(`/api/crm/contatos?${buildQuery(session)}`, {
      method: 'POST', headers: jsonHeaders,
      body: JSON.stringify({ ...payload, empresaId: getEmpresaId(session) })
    });
    const data = await handle(r);
    return data.contato;
  },
  atualizarContato: async (session: any, id: string, payload: Partial<Contato>): Promise<void> => {
    const r = await fetch(`/api/crm/contatos/${id}?${buildQuery(session)}`, {
      method: 'PUT', headers: jsonHeaders,
      body: JSON.stringify({ ...payload, empresaId: getEmpresaId(session) })
    });
    await handle(r);
  },
  removerContato: async (session: any, id: string): Promise<void> => {
    const r = await fetch(`/api/crm/contatos/${id}?${buildQuery(session)}`, { method: 'DELETE' });
    await handle(r);
  },

  // Leads
  listarLeads: async (session: any): Promise<Lead[]> => {
    const r = await fetch(`/api/crm/leads?${buildQuery(session)}`);
    const data = await handle(r);
    return data.leads || [];
  },
  criarLead: async (session: any, payload: Partial<Lead>): Promise<Lead> => {
    const r = await fetch(`/api/crm/leads?${buildQuery(session)}`, {
      method: 'POST', headers: jsonHeaders,
      body: JSON.stringify({ ...payload, empresaId: getEmpresaId(session) })
    });
    const data = await handle(r);
    return data.lead;
  },
  atualizarLead: async (session: any, id: string, payload: Partial<Lead>): Promise<void> => {
    const r = await fetch(`/api/crm/leads/${id}?${buildQuery(session)}`, {
      method: 'PUT', headers: jsonHeaders,
      body: JSON.stringify({ ...payload, empresaId: getEmpresaId(session) })
    });
    await handle(r);
  },
  removerLead: async (session: any, id: string): Promise<void> => {
    const r = await fetch(`/api/crm/leads/${id}?${buildQuery(session)}`, { method: 'DELETE' });
    await handle(r);
  },

  // Etiquetas
  listarEtiquetas: async (session: any): Promise<Etiqueta[]> => {
    const r = await fetch(`/api/crm/etiquetas?${buildQuery(session)}`);
    const data = await handle(r);
    return data.etiquetas || [];
  },
  criarEtiqueta: async (session: any, nome: string, cor: string): Promise<Etiqueta> => {
    const r = await fetch(`/api/crm/etiquetas?${buildQuery(session)}`, {
      method: 'POST', headers: jsonHeaders,
      body: JSON.stringify({ empresaId: getEmpresaId(session), nome, cor })
    });
    const data = await handle(r);
    return data.etiqueta;
  },
  removerEtiqueta: async (session: any, id: string): Promise<void> => {
    const r = await fetch(`/api/crm/etiquetas/${id}?${buildQuery(session)}`, { method: 'DELETE' });
    await handle(r);
  },

  // Campos Customizados
  listarCamposCustom: async (session: any, entidade?: string): Promise<CampoCustom[]> => {
    const extra = entidade ? { entidade } : {};
    const r = await fetch(`/api/crm/campos-custom?${buildQuery(session, extra)}`);
    const data = await handle(r);
    return data.campos || [];
  },
  criarCampoCustom: async (session: any, payload: Partial<CampoCustom>): Promise<CampoCustom> => {
    const r = await fetch(`/api/crm/campos-custom?${buildQuery(session)}`, {
      method: 'POST', headers: jsonHeaders,
      body: JSON.stringify({ empresaId: getEmpresaId(session), ...payload })
    });
    const data = await handle(r);
    return data.campo;
  },
  atualizarCampoCustom: async (session: any, id: string, payload: Partial<CampoCustom>): Promise<void> => {
    const r = await fetch(`/api/crm/campos-custom/${id}?${buildQuery(session)}`, {
      method: 'PUT', headers: jsonHeaders,
      body: JSON.stringify(payload)
    });
    await handle(r);
  },
  removerCampoCustom: async (session: any, id: string): Promise<void> => {
    const r = await fetch(`/api/crm/campos-custom/${id}?${buildQuery(session)}`, { method: 'DELETE' });
    await handle(r);
  },

  // Empresas-Conta
  listarEmpresasConta: async (session: any): Promise<EmpresaConta[]> => {
    const r = await fetch(`/api/crm/empresas-conta?${buildQuery(session)}`);
    const data = await handle(r);
    return data.empresas || [];
  },
  criarEmpresaConta: async (session: any, payload: Partial<EmpresaConta>): Promise<EmpresaConta> => {
    const r = await fetch(`/api/crm/empresas-conta?${buildQuery(session)}`, {
      method: 'POST', headers: jsonHeaders,
      body: JSON.stringify({ ...payload, empresaId: getEmpresaId(session) }),
    });
    const data = await handle(r);
    return data.empresa;
  },
  atualizarEmpresaConta: async (session: any, id: string, payload: Partial<EmpresaConta>): Promise<void> => {
    const r = await fetch(`/api/crm/empresas-conta/${id}?${buildQuery(session)}`, {
      method: 'PUT', headers: jsonHeaders,
      body: JSON.stringify(payload),
    });
    await handle(r);
  },
  removerEmpresaConta: async (session: any, id: string): Promise<void> => {
    const r = await fetch(`/api/crm/empresas-conta/${id}?${buildQuery(session)}`, { method: 'DELETE' });
    await handle(r);
  },

  // Converter Lead em Contato + Empresa-Conta + (opcional) Oportunidade
  converterLead: async (
    session: any,
    leadId: string,
    payload: {
      empresaContaId?: string | null;
      criarEmpresa?: boolean;
      empresaContaNova?: Partial<EmpresaConta>;
      contatoId?: string | null;
      cargo?: string;
      tipoContato?: Contato['tipo'];
      criarOportunidade?: boolean;
      oportunidade?: { funilId: string; etapaId: string; titulo?: string; valor?: number; dataFechamentoPrevista?: string | null };
    }
  ): Promise<{ contatoId: string; empresaContaId: string | null; negocioId: string | null }> => {
    const r = await fetch(`/api/crm/leads/${leadId}/converter?${buildQuery(session)}`, {
      method: 'POST', headers: jsonHeaders,
      body: JSON.stringify({ ...payload, empresaId: getEmpresaId(session) }),
    });
    const data = await handle(r);
    return { contatoId: data.contatoId, empresaContaId: data.empresaContaId, negocioId: data.negocioId };
  },

  // Minha Carteira
  minhaCarteira: async (session: any, responsavelId?: string): Promise<any> => {
    const extra: Record<string, string> = {};
    if (responsavelId) extra.responsavelId = responsavelId;
    const r = await fetch(`/api/crm/minha-carteira?${buildQuery(session, extra)}`);
    const data = await handle(r);
    return data;
  },

  // ---------------- Atividades (Timeline + Tarefas) ----------------
  atividades: {
    listar: async (session: any, vinculo: { leadId?: string; contatoId?: string; empresaContaId?: string; negocioId?: string }, opts: { tipo?: string; pendentes?: boolean; responsavelId?: string; limit?: number } = {}): Promise<any[]> => {
      const extra: Record<string, string> = {};
      Object.entries(vinculo).forEach(([k, v]) => { if (v) extra[k] = v as string; });
      if (opts.tipo) extra.tipo = opts.tipo;
      if (opts.pendentes) extra.pendentes = 'true';
      if (opts.responsavelId) extra.responsavelId = opts.responsavelId;
      if (opts.limit) extra.limit = String(opts.limit);
      const r = await fetch(`/api/crm/atividades?${buildQuery(session, extra)}`);
      const data = await handle(r);
      return data.atividades || [];
    },
    criar: async (session: any, payload: any): Promise<any> => {
      const r = await fetch(`/api/crm/atividades?${buildQuery(session)}`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ ...payload, empresaId: getEmpresaId(session) }),
      });
      return (await handle(r)).atividade;
    },
    atualizar: async (session: any, id: string, payload: any): Promise<any> => {
      const r = await fetch(`/api/crm/atividades/${id}?${buildQuery(session)}`, {
        method: 'PUT', headers: jsonHeaders,
        body: JSON.stringify(payload),
      });
      return (await handle(r)).atividade;
    },
    deletar: async (session: any, id: string): Promise<void> => {
      const r = await fetch(`/api/crm/atividades/${id}?${buildQuery(session)}`, { method: 'DELETE' });
      await handle(r);
    },
    tarefasPendentes: async (session: any, opts: { responsavelId?: string; horizonte?: 'hoje' | 'semana' | 'todas' } = {}): Promise<{ tarefas: any[]; totais: any }> => {
      const extra: Record<string, string> = {};
      if (opts.responsavelId) extra.responsavelId = opts.responsavelId;
      if (opts.horizonte) extra.horizonte = opts.horizonte;
      const r = await fetch(`/api/crm/tarefas-pendentes?${buildQuery(session, extra)}`);
      const data = await handle(r);
      return { tarefas: data.tarefas || [], totais: data.totais || {} };
    },
  },

  // ---------------- Templates de WhatsApp ----------------
  templates: {
    listar: async (session: any): Promise<any[]> => {
      const r = await fetch(`/api/crm/templates?${buildQuery(session)}`);
      return (await handle(r)).templates || [];
    },
    criar: async (session: any, payload: any): Promise<any> => {
      const r = await fetch(`/api/crm/templates?${buildQuery(session)}`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ ...payload, empresaId: getEmpresaId(session) }),
      });
      return (await handle(r)).template;
    },
    atualizar: async (session: any, id: string, payload: any): Promise<any> => {
      const r = await fetch(`/api/crm/templates/${id}?${buildQuery(session)}`, {
        method: 'PUT', headers: jsonHeaders,
        body: JSON.stringify(payload),
      });
      return (await handle(r)).template;
    },
    deletar: async (session: any, id: string): Promise<void> => {
      const r = await fetch(`/api/crm/templates/${id}?${buildQuery(session)}`, { method: 'DELETE' });
      await handle(r);
    },
  },

  // ---------------- WhatsApp CRM ----------------
  whatsapp: {
    enviar: async (session: any, payload: { numero?: string; mensagem?: string; templateId?: string; variaveis?: any; leadId?: string; contatoId?: string; empresaContaId?: string; negocioId?: string; quotedMessageId?: string }): Promise<any> => {
      const r = await fetch(`/api/crm/whatsapp/enviar?${buildQuery(session)}`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ ...payload, empresaId: getEmpresaId(session) }),
      });
      return await handle(r);
    },
    enviarLote: async (session: any, payload: { destinatarios: any[]; mensagem?: string; templateId?: string; variaveis?: any }): Promise<any> => {
      const r = await fetch(`/api/crm/whatsapp/enviar-lote?${buildQuery(session)}`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ ...payload, empresaId: getEmpresaId(session) }),
      });
      return await handle(r);
    },
    inbox: async (session: any, naoLidas = false): Promise<any[]> => {
      const extra: Record<string, string> = {};
      if (naoLidas) extra.naoLidas = 'true';
      const r = await fetch(`/api/crm/whatsapp/inbox?${buildQuery(session, extra)}`);
      return (await handle(r)).conversas || [];
    },
    conversa: async (session: any, numero: string): Promise<any[]> => {
      const r = await fetch(`/api/crm/whatsapp/conversa?${buildQuery(session, { numero })}`);
      return (await handle(r)).mensagens || [];
    },
    apagarConversa: async (session: any, numero: string): Promise<any> => {
      const r = await fetch(`/api/crm/whatsapp/conversa?${buildQuery(session, { numero })}`, { method: 'DELETE' });
      return await handle(r);
    },
    converterEmLead: async (session: any, payload: { numero: string; nome: string; empresa?: string; email?: string; observacoes?: string; funilId?: string; etapaId?: string; temperatura?: string; valor?: number }): Promise<any> => {
      const r = await fetch(`/api/crm/whatsapp/converter-em-lead?${buildQuery(session)}`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({ ...payload, empresaId: getEmpresaId(session) }),
      });
      return await handle(r);
    },
    minhaSessao: {
      status: async (session: any): Promise<any> => {
        const r = await fetch(`/api/crm/whatsapp/minha-sessao/status?${buildQuery(session)}`);
        return await handle(r);
      },
      qr: async (session: any): Promise<any> => {
        const r = await fetch(`/api/crm/whatsapp/minha-sessao/qr?${buildQuery(session)}`);
        return await handle(r);
      },
      conectar: async (session: any): Promise<any> => {
        const r = await fetch(`/api/crm/whatsapp/minha-sessao/conectar?${buildQuery(session)}`, {
          method: 'POST', headers: jsonHeaders, body: JSON.stringify({}),
        });
        return await handle(r);
      },
      desconectar: async (session: any): Promise<any> => {
        const r = await fetch(`/api/crm/whatsapp/minha-sessao/desconectar?${buildQuery(session)}`, {
          method: 'POST', headers: jsonHeaders, body: JSON.stringify({}),
        });
        return await handle(r);
      },
    },
  },
};
